import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';

interface IValue<T> {
  value: T;
  loading: boolean;
  error?: string;
}

/**
 * `CollectionState`'s sibling for domains holding one state object rather than a
 * list. Same deal: not a state-management library, just a `BehaviorSubject` with
 * the loading/error handling the old `CustomStore` base provided.
 *
 * Compose it in a domain service rather than subclassing.
 */
export class ValueState<T> {
  private readonly state$: BehaviorSubject<IValue<T>>;
  private readonly initial: T;

  public readonly value$: Observable<T>;
  public readonly loading$: Observable<boolean>;
  public readonly error$: Observable<string | undefined>;

  public constructor(initial: T, loading = false) {
    this.initial = initial;
    this.state$ = new BehaviorSubject<IValue<T>>({ loading, value: initial });

    this.value$ = this.select(({ value }) => value);
    this.loading$ = this.select(({ loading: isLoading }) => isLoading);
    this.error$ = this.select(({ error }) => error);
  }

  public get value(): T {
    return this.state$.value.value;
  }

  public select<R>(project: (state: IValue<T>) => R): Observable<R> {
    return this.state$.pipe(map(project), distinctUntilChanged());
  }

  public selectValue<R>(project: (value: T) => R): Observable<R> {
    return this.select(({ value }) => project(value));
  }

  public set(value: T): void {
    this.patch({ loading: false, value });
  }

  // Constrained on `this`, not the class, so ValueState<Thing | undefined> stays
  // usable via `set` (part-design does exactly that) while a spread of `undefined`
  // becomes a compile error rather than a runtime throw.
  public update(
    this: ValueState<T & object>,
    partialOrFn: Partial<T> | ((value: T) => Partial<T>)
  ): void {
    const current = this.value;
    const partial = partialOrFn instanceof Function ? partialOrFn(current) : partialOrFn;

    this.patch({ value: { ...current, ...partial } });
  }

  public setLoading(loading: boolean): void {
    this.patch(loading ? { error: undefined, loading } : { loading });
  }

  /**
   * Ends the load, unlike `CollectionState.setError`. The two Akita bases really
   * did differ here — `CustomStore` forced `loading` false, `CustomEntityStore`
   * left it alone — and this one replaces `CustomStore`.
   */
  public setError(error: string | undefined): void {
    this.patch({ error, loading: false });
  }

  public reset(): void {
    this.state$.next({ loading: false, value: this.initial });
  }

  private patch(partial: Partial<IValue<T>>): void {
    this.state$.next({ ...this.state$.value, ...partial });
  }
}
